import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    fetchFoulWords,
    editFoulWords,
    addFoulWords,
    improveFoulWordsByAI,
    deleteFoulWord,
} from "services/apis";

// 🔹 Initial state
const initialState = {
    foulWords: [],
    improvedFoulWords: [],
    showingImproved: false,
    page: 1,
    totalCount: 0,
    // Status
    loading: false,
    status: "idle",
    ImprovementLoading: false,
    // Error states per action
    fetchError: null,
    updateError: null,
    addError: null,
    deleteError: null,
    improveError: null,
};

// 🔹 Async Thunks
// export const getFoulWords = createAsyncThunk(
//     "foulWords/fetchFoulWords",
//     async (_, { rejectWithValue }) => {
//         try {
//             const response = await fetchFoulWords();
//             return response.data;
//         } catch (error) {
//             return rejectWithValue(error.message || "Failed to fetch foul words");
//         }
//     }
// );

export const getFoulWords = createAsyncThunk(
    "foulWords/fetchFoulWords",
    async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
        try {
            const response = await fetchFoulWords({ page, limit }); // response should include data and totalCount
            return { data: response.data.foulWordList, totalCount: response.data.totalCount, page };
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch foul words");
        }
    }
);

export const updateFoulWord = createAsyncThunk(
    "foulWords/updateFoulWord",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await editFoulWords(payload);
            return payload;
        } catch (error) {
            return rejectWithValue(error.message || "Failed to update foul word");
        }
    }
);

export const addFoulWord = createAsyncThunk(
    "foulWords/addFoulWord",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await addFoulWords(payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || "Failed to add foul word");
        }
    }
);

export const improveFoulWords = createAsyncThunk(
    "foulWords/improveFoulWords",
    async (_, { rejectWithValue }) => {
        try {
            const response = await improveFoulWordsByAI();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || "Failed to improve foul words by AI");
        }
    }
);

export const removeFoulWord = createAsyncThunk(
    "foulWords/deleteFoulWord",
    async (id, { rejectWithValue }) => {
        try {
            await deleteFoulWord(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.message || "Failed to delete foul word");
        }
    }
);

// 🔹 Slice
const foulWordsSlice = createSlice({
    name: "foulWords",
    initialState,
    reducers: {
        cancelImprovement: (state) => {
            state.improvedFoulWords = [];
            state.showingImproved = false;
        },
        acceptImprovement: (state) => {
            // state.foulWords = state.improvedFoulWords;
            state.improvedFoulWords = [];
            state.showingImproved = false;
        },
        clearErrors: (state) => {
            state.fetchError = null;
            state.updateError = null;
            state.addError = null;
            state.deleteError = null;
            state.improveError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH
            .addCase(getFoulWords.pending, (state) => {
                state.loading = true;
                state.status = "loading";
                state.fetchError = null;
            })
            // .addCase(getFoulWords.fulfilled, (state, action) => {
            //     state.loading = false;
            //     state.status = "succeeded";
            //     state.foulWords = action.payload;
            // })
            .addCase(getFoulWords.fulfilled, (state, action) => {
                const { data, totalCount, page } = action.payload;
                state.loading = false;
                state.page = page;
                state.totalCount = totalCount;

                if (page === 1) {
                    state.foulWords = data;
                } else {
                    state.foulWords = [...state.foulWords, ...data];
                }
            })
            .addCase(getFoulWords.rejected, (state, action) => {
                state.loading = false;
                state.status = "failed";
                state.fetchError = action.payload;
            })

            // UPDATE
            .addCase(updateFoulWord.fulfilled, (state, action) => {
                const { wordId, updatedfoulWord } = action.payload;
                state.foulWords = state.foulWords.map((word) =>
                    word.id === wordId ? { ...word, foulWord: updatedfoulWord } : word
                );
            })

            .addCase(updateFoulWord.rejected, (state, action) => {
                state.updateError = action.payload;
            })

            // ADD
            // .addCase(addFoulWord.fulfilled, (state, action) => {
            //     state.foulWords = action.payload;
            // })
            .addCase(addFoulWord.rejected, (state, action) => {
                state.addError = action.payload;
            })

            // DELETE
            .addCase(removeFoulWord.fulfilled, (state, action) => {
                const idToRemove = action.payload;
                state.foulWords = state.foulWords.filter((word) => word.id !== idToRemove);
            })
            .addCase(removeFoulWord.rejected, (state, action) => {
                state.deleteError = action.payload;
            })

            // IMPROVE
            .addCase(improveFoulWords.pending, (state) => {
                state.ImprovementLoading = true;
                state.showingImproved = true;
                state.improveError = null;
            })
            .addCase(improveFoulWords.fulfilled, (state, action) => {
                state.ImprovementLoading = false;
                state.improvedFoulWords = action.payload;
                state.showingImproved = true;
            })
            .addCase(improveFoulWords.rejected, (state, action) => {
                state.ImprovementLoading = false;
                state.improveError = action.payload;
                state.showingImproved = false;
            });
    },
});

// 🔹 Export actions & reducer
export const { cancelImprovement, acceptImprovement, clearErrors } = foulWordsSlice.actions;
export default foulWordsSlice.reducer;
